/**
 * @module UploadReceiptModalStagedFlow
 * @purpose Composes the transport probe and staged-registration sub-hooks
 *   into the exact field set `useUploadReceiptModal` re-exposes, keeping
 *   that hook's own body from growing past the function-length budget.
 * @depends upload-receipt-modal.probe, upload-receipt-modal.staged
 * @dependedOnBy use-upload-receipt-modal
 */

import { useCallback } from 'react'
import type { TFunction } from 'i18next'
import type { CollectionReceipt } from '../../../types/collection-receipts'
import type { CollectionTarget } from '../../../types/collection-targets'
import type { OperationProgress, ProbeReceiptResponse } from '../../../types/evidence-containers'
import { useTransportProbe } from './upload-receipt-modal.probe'
import { useStagedRegistration, type StagedStep } from './upload-receipt-modal.staged'

export interface UseStagedFlowArgs {
  caseId: string
  requestId: string
  zipFile: File | null
  target: CollectionTarget | null
  oobHash: string
  notes: string
  onSuccess: (receipt: CollectionReceipt) => void
  t: TFunction<'cases'>
}

export interface UseStagedFlowResult {
  transportProbe: ProbeReceiptResponse | null
  probing: boolean
  probeError: string | null
  retryProbe: () => void
  stagedStep: StagedStep
  stagedOperationId: string | null
  stagedError: string | null
  stagedAlreadyExists: boolean
  handleRegisterStaged: () => Promise<void>
  handleStagedVerifyComplete: (progress: OperationProgress) => Promise<void>
  retryStagedVerification: () => Promise<void>
  resetStaged: () => void
}

export function useStagedFlow(args: UseStagedFlowArgs): UseStagedFlowResult {
  const { caseId, requestId, zipFile, target, oobHash, notes, onSuccess, t } = args
  const probe = useTransportProbe(caseId, requestId, zipFile)
  const staged = useStagedRegistration(onSuccess, t)

  const handleRegisterStaged = useCallback(async () => {
    const fileName = probe.transportProbe?.stagingInstructions?.expectedFileName
    if (!target || !fileName) return
    await staged.register({ caseId, requestId, target, fileName, oobHash, notes })
  }, [probe.transportProbe, target, caseId, requestId, oobHash, notes, staged])

  return {
    transportProbe: probe.transportProbe,
    probing: probe.probing,
    probeError: probe.probeError,
    retryProbe: probe.retryProbe,
    stagedStep: staged.step,
    stagedOperationId: staged.operationId,
    stagedError: staged.error,
    stagedAlreadyExists: staged.alreadyExists,
    handleRegisterStaged,
    handleStagedVerifyComplete: staged.handleVerifyComplete,
    retryStagedVerification: staged.retryVerification,
    resetStaged: staged.reset,
  }
}
